/**
 * Every keyboard shortcut in the app. Ids are prefixed with the context they work in.
 *
 * `keys` format: "meta+shift+f" where
 * - meta = Cmd on Mac, Ctrl on Windows/Linux
 * - shift, alt, ctrl can be combined
 * - the last part is the key (e.g. 'f', ',', 'ArrowUp')
 *
 * `sequence`: keys pressed one after another (Linear-style), outside text fields.
 */

export type Shortcut =
  | { label: string; keys: string; sequence?: never }
  | { label: string; sequence: readonly string[]; keys?: never };

// Helper to detect Mac platform, safe for SSR
const getIsMac = () => {
  if (typeof navigator === "undefined") return true; // Default to Mac for SSR
  return navigator.platform.toUpperCase().indexOf("MAC") >= 0;
};

export const KEYBOARD_SHORTCUTS = {
  "global-command-bar": { label: "Open command bar", keys: "meta+k" },
  "global-settings": { label: "Open settings", keys: "meta+," },
  "global-switch-customer": { label: "Switch customer", sequence: ["s", "c"] },

  "table-filter": { label: "Add filter", keys: "meta+shift+f" },
  "table-select-all": { label: "Select all cells", keys: "meta+a" },
  "table-copy": { label: "Copy selected cells", keys: "meta+c" },
  "table-row-start": { label: "Jump to row start", keys: "meta+ArrowLeft" },
  "table-row-end": { label: "Jump to row end", keys: "meta+ArrowRight" },
  "table-column-top": { label: "Jump to column top", keys: "meta+ArrowUp" },
  "table-column-bottom": { label: "Jump to column bottom", keys: "meta+ArrowDown" },
  "table-open-record": { label: "Open record", keys: "Enter" },
  "table-save-all-edits": { label: "Save all edits", keys: "meta+Enter" },
  "table-promote-cells-to-rows": { label: "Select whole rows", keys: "shift+ " },

  "record-modal-close": { label: "Close record", keys: "Escape" },

  "proposal-list-clear-selection": { label: "Clear selection", keys: "Escape" },
  "proposal-list-select-all": { label: "Select all proposals", keys: "meta+a" },
  "proposal-list-fit-width": { label: "Fit to width", keys: "!" },
  "proposal-list-fit-selection": { label: "Zoom to selection", keys: "@" },

  "proposal-review-previous": { label: "Previous proposal", keys: "ArrowLeft" },
  "proposal-review-next": { label: "Next proposal", keys: "ArrowRight" },
} as const satisfies Record<string, Shortcut>;

export type ShortcutId = keyof typeof KEYBOARD_SHORTCUTS;

const isSymbolKey = (key: string) => key.length === 1 && !/[a-z0-9 ]/.test(key);

export function matchesShortcut(event: KeyboardEvent, shortcut: Shortcut): boolean {
  if (!shortcut.keys) return false;
  const isMac = getIsMac();

  // Parse the shortcut string
  const parts = shortcut.keys.toLowerCase().split("+");
  const key = parts[parts.length - 1]; // Last part is always the key

  // Check modifiers
  const requiresMeta = parts.includes("meta");
  const requiresShift = parts.includes("shift");
  const requiresAlt = parts.includes("alt");
  const requiresCtrl = parts.includes("ctrl");

  // Check if meta/ctrl is pressed
  const metaKey = isMac ? event.metaKey : event.ctrlKey;

  // If meta is required, check metaKey
  // If ctrl is required (not meta), check event.ctrlKey directly
  const metaPressed = requiresMeta ? metaKey : true;
  const ctrlPressed = requiresCtrl ? event.ctrlKey : true;

  // Symbols like "!" or "@" need shift/alt on some layouts; the produced key is what counts.
  const symbol = isSymbolKey(key);
  const shiftOk = symbol && !requiresShift ? true : event.shiftKey === requiresShift;
  const altOk = symbol && !requiresAlt ? true : event.altKey === requiresAlt;

  return event.key.toLowerCase() === key && metaPressed && ctrlPressed && shiftOk && altOk;
}

export function isTypingTarget(target: EventTarget | null): boolean {
  const t = target as HTMLElement | null;
  return (
    !!t &&
    (t.tagName === "INPUT" ||
      t.tagName === "TEXTAREA" ||
      t.tagName === "SELECT" ||
      t.isContentEditable)
  );
}

const SEQUENCE_TIMEOUT_MS = 1000;

/**
 * Returns a keydown handler that calls `onMatch` when one of the given sequence
 * shortcuts is completed. Ignores keys typed into text fields.
 */
export function createSequenceListener<Id extends ShortcutId>(
  ids: readonly Id[],
  onMatch: (id: Id) => void,
): (event: KeyboardEvent) => void {
  const sequences = ids.map((id) => {
    const shortcut: Shortcut = KEYBOARD_SHORTCUTS[id];
    return { id, keys: shortcut.sequence ?? [] };
  });
  const maxLength = Math.max(...sequences.map((s) => s.keys.length));
  let typed: string[] = [];
  let lastAt = 0;

  return (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.defaultPrevented || event.repeat)
      return;
    if (isTypingTarget(event.target)) {
      typed = [];
      return;
    }

    const now = Date.now();
    if (now - lastAt > SEQUENCE_TIMEOUT_MS) typed = [];
    lastAt = now;
    typed = [...typed, event.key.toLowerCase()].slice(-maxLength);

    const match = sequences.find(
      ({ keys }) =>
        keys.length > 0 && keys.every((k, i) => typed[typed.length - keys.length + i] === k),
    );
    if (match) {
      event.preventDefault();
      typed = [];
      onMatch(match.id);
    }
  };
}

/** Keys to render as separate <kbd>s, e.g. ["⌘", "K"] or ["S", "C"]. */
export function getShortcutKeys(shortcut: Shortcut): string[] {
  if (shortcut.sequence) return shortcut.sequence.map((k) => k.toUpperCase());
  return [getShortcutDisplay(shortcut)];
}

/**
 * Get the display string for a shortcut
 */
export function getShortcutDisplay(shortcut: Shortcut): string {
  if (shortcut.sequence) return shortcut.sequence.map((k) => k.toUpperCase()).join(" ");
  const isMac = getIsMac();
  const parts = shortcut.keys.toLowerCase().split("+");

  const displayParts: string[] = [];

  // Convert meta to platform-specific symbol
  if (parts.includes("meta")) {
    displayParts.push(isMac ? "⌘" : "Ctrl");
  }

  // Add other modifiers
  if (parts.includes("shift")) {
    displayParts.push("⇧");
  }
  if (parts.includes("alt")) {
    displayParts.push("⌥");
  }
  if (parts.includes("ctrl")) {
    displayParts.push("⌃");
  }

  // Add the key (last part)
  const key = parts[parts.length - 1];
  displayParts.push(key.toUpperCase());

  return displayParts.join("");
}
